import type { ServerProvider } from "@cmmd-forge/contracts";

import { resolveConnectedAccountEmail } from "../../lib/connectedAccountEmail";
import { FIRST_ACCOUNT_SETUP_STARTER_PROMPT } from "../../lib/firstAccountSetupStarterPrompt";

export { FIRST_ACCOUNT_SETUP_STARTER_PROMPT };

export type SetupStepId =
  | "environment"
  | "account"
  | "usage"
  | "providers"
  | "projects"
  | "capabilities"
  | "first-run";
export type UsageSource = "workspace" | "personal";

export interface SetupStep {
  readonly id: SetupStepId;
  readonly label: string;
  readonly detail: string;
}

export interface ProviderSetupRow {
  readonly id: ServerProvider["provider"];
  readonly name: string;
  readonly detail: string;
  readonly credentialSource: string;
  readonly credentialKind: "workspace" | "personal" | "environment";
  readonly accountEmail: string | null;
  readonly statusLabel: string;
  readonly statusTone: "ready" | "attention" | "neutral";
  readonly authAction: "connect-codex" | "connect-claude" | null;
}

export interface SetupProjectRow {
  readonly id: string;
  readonly source: "forge" | "cmmd";
  readonly setupAction: "open" | "import" | "link-repo";
  readonly environmentId: string;
  readonly projectId: string;
  readonly name: string;
  readonly cwd: string;
  readonly availability: "available" | "missing" | "unknown";
  readonly defaultProviderLabel: string;
  readonly environmentLabel: string;
  readonly environmentIconUrl?: string | null;
  readonly projectIcon?: string | null;
  readonly projectIconUrl?: string | null;
  readonly githubRepoUrl?: string | null;
  readonly workspaceSummary?: string | null;
}

export interface SetupProjectWorkspaceOption {
  readonly id: string;
  readonly label: string;
  readonly iconUrl: string | null;
}

export function isSetupProjectOpenable(project: SetupProjectRow): boolean {
  return project.setupAction === "open" && project.availability === "available";
}

export interface StepContent {
  readonly id: SetupStepId;
  readonly eyebrow: string;
  readonly title: string;
  readonly description: string;
  readonly badge: string;
}

export const SETUP_STEPS: readonly SetupStep[] = [
  { id: "environment", label: "Environment", detail: "Ensure your Seat Environment" },
  { id: "account", label: "Appearance", detail: "Theme and font size" },
  { id: "usage", label: "Agents", detail: "Codex and Claude access" },
  { id: "capabilities", label: "Plugins", detail: "GitHub and app context" },
  { id: "projects", label: "Workspace", detail: "Import your work" },
  { id: "first-run", label: "First chat", detail: "Start with an agent" },
];

export const SETUP_CONTENT: Readonly<Record<SetupStepId, StepContent>> = {
  environment: {
    id: "environment",
    eyebrow: "Environment",
    title: "Prepare your Environment.",
    description: "CMMD ensures the Environment for this Seat before Workspace import.",
    badge: "Seat",
  },
  account: {
    id: "account",
    eyebrow: "Appearance",
    title: "Make Forge feel right.",
    description: "Choose the theme and text size for this device.",
    badge: "Theme",
  },
  usage: {
    id: "usage",
    eyebrow: "Agents",
    title: "Choose how agents run.",
    description: "Use project credits, or connect your own Codex and Claude subscriptions.",
    badge: "Accounts",
  },
  providers: {
    id: "providers",
    eyebrow: "Agents",
    title: "Connect providers.",
    description: "Forge can use multiple agents. Connect what you want available.",
    badge: "Multi-provider",
  },
  projects: {
    id: "projects",
    eyebrow: "Workspace",
    title: "Choose where to start.",
    description: "Open a Forge project now, or prepare a CMMD project for later.",
    badge: "Workspace",
  },
  capabilities: {
    id: "capabilities",
    eyebrow: "Plugins",
    title: "Connect the apps you use.",
    description: "",
    badge: "Apps",
  },
  "first-run": {
    id: "first-run",
    eyebrow: "First chat",
    title: "Start your first agent chat.",
    description: "Save setup and open Forge.",
    badge: "Ready",
  },
};

export function providerDisplayName(provider: string | null | undefined): string {
  switch (provider) {
    case "codex":
      return "Codex";
    case "claudeAgent":
      return "Claude";
    case "apex":
      return "Apex";
    case "cursor":
      return "Cursor";
    case "opencode":
      return "OpenCode";
    case "ollama":
      return "Ollama";
    case "cmmd":
      return "CMMD";
    case "":
    case null:
    case undefined:
      return "Not set";
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

function providerDetail(provider: ServerProvider["provider"]): string {
  switch (provider) {
    case "codex":
      return "Primary coding agent";
    case "claudeAgent":
      return "Optional secondary agent";
    case "apex":
      return "Shared API-backed model route";
    case "cursor":
      return "Cursor-compatible local agent";
    case "opencode":
      return "Open local coding runtime";
    case "ollama":
      return "Local model runtime";
    case "cmmd":
      return "CMMD planning context";
  }
}

function credentialSource(
  provider: ServerProvider,
): Pick<ProviderSetupRow, "credentialKind" | "credentialSource"> {
  const provenance = provider.auth.provenance;
  if (provider.provider === "apex" || provider.provider === "cmmd") {
    return { credentialKind: "workspace", credentialSource: "Workspace managed" };
  }
  if (provenance?.credentialScope === "proxied") {
    return { credentialKind: "workspace", credentialSource: "Workspace managed" };
  }
  if (provider.auth.status === "authenticated") {
    return {
      credentialKind: "personal",
      credentialSource:
        provider.auth.label ?? provider.auth.accountEmail ?? "Personal subscription",
    };
  }
  if (provenance?.source === "environment") {
    return { credentialKind: "environment", credentialSource: "Environment default" };
  }
  return { credentialKind: "personal", credentialSource: "Personal subscription" };
}

/**
 * Mirrors `resolveCodexAccountIdentity` in SettingsPanels — one identity source for
 * local CLI auth and the CMMD user store, validated as an email address.
 */
function resolveProviderAccountEmail(
  provider: ServerProvider,
  cloudCredential: CloudCodexCredentialSummary | null | undefined,
): string | null {
  if (provider.auth.status === "authenticated") {
    return resolveConnectedAccountEmail(
      provider.auth.accountEmail ?? provider.auth.provenance?.accountLabel ?? null,
    );
  }
  if (cloudCredential?.connected === true) {
    return resolveConnectedAccountEmail(cloudCredential.accountLabel);
  }
  return null;
}

function providerStatus(
  provider: ServerProvider,
): Pick<ProviderSetupRow, "statusLabel" | "statusTone"> {
  if (
    provider.enabled === true &&
    provider.installed === true &&
    provider.status === "ready" &&
    provider.auth.status === "authenticated"
  ) {
    return { statusLabel: "Ready", statusTone: "ready" };
  }

  if (provider.status === "error" || provider.auth.status === "unauthenticated") {
    return { statusLabel: "Needs auth", statusTone: "attention" };
  }

  if (provider.enabled === false || provider.status === "disabled") {
    return { statusLabel: "Disabled", statusTone: "neutral" };
  }

  return { statusLabel: "Available", statusTone: "ready" };
}

function providerAuthAction(provider: ServerProvider): ProviderSetupRow["authAction"] {
  if (provider.auth.status === "authenticated") {
    return null;
  }
  if (provider.provider === "codex") {
    return "connect-codex";
  }
  if (provider.provider === "claudeAgent") {
    return "connect-claude";
  }
  return null;
}

/**
 * Card 1359 — a personal Codex/Claude credential lives in the CMMD USER store.
 * On a cloud-only surface the LOCAL CLI is never "authenticated", so without this
 * the setup step would show "Connect personal" even for a user who already
 * connected (here, on the desktop app, or on another device). Reflect the store:
 * a connected credential makes the row read as personally connected, with no
 * connect action. Local auth still wins (that path returns early). The shape is
 * provider-generic (used for both codex and claudeAgent).
 */
export interface CloudCodexCredentialSummary {
  readonly connected: boolean;
  readonly accountLabel: string | null;
}

function applyCloudProviderCredential(
  row: ProviderSetupRow,
  provider: ServerProvider,
  cloudCodex: CloudCodexCredentialSummary | null | undefined,
  cloudClaude: CloudCodexCredentialSummary | null | undefined,
): ProviderSetupRow {
  if (provider.auth.status === "authenticated") return row;
  const cloud =
    provider.provider === "codex"
      ? cloudCodex
      : provider.provider === "claudeAgent"
        ? cloudClaude
        : null;
  if (!cloud?.connected) return row;
  return {
    ...row,
    credentialKind: "personal",
    credentialSource: cloud.accountLabel ?? "Personal subscription",
    accountEmail: resolveConnectedAccountEmail(cloud.accountLabel),
    statusLabel: "Ready",
    statusTone: "ready",
    authAction: null,
  };
}

export function buildProviderRows(
  providers: ReadonlyArray<ServerProvider>,
  cloudCodex?: CloudCodexCredentialSummary | null,
  cloudClaude?: CloudCodexCredentialSummary | null,
): readonly ProviderSetupRow[] {
  const visibleProviders = providers.filter(
    (provider) =>
      provider.provider !== "cmmd" &&
      provider.provider !== "cursor" &&
      provider.provider !== "opencode" &&
      provider.provider !== "ollama",
  );
  return visibleProviders.map((provider) => {
    const status = providerStatus(provider);
    const credential = credentialSource(provider);
    const cloudCredential =
      provider.provider === "codex"
        ? cloudCodex
        : provider.provider === "claudeAgent"
          ? cloudClaude
          : null;
    const row: ProviderSetupRow = {
      id: provider.provider,
      name: providerDisplayName(provider.provider),
      detail: providerDetail(provider.provider),
      credentialSource: credential.credentialSource,
      credentialKind: credential.credentialKind,
      accountEmail: resolveProviderAccountEmail(provider, cloudCredential),
      statusLabel: status.statusLabel,
      statusTone: status.statusTone,
      authAction: providerAuthAction(provider),
    };
    return applyCloudProviderCredential(row, provider, cloudCodex, cloudClaude);
  });
}
